import React, { useEffect, useRef, useState } from 'react';

import { Avatar } from '@change/ds-core-ui/Avatar';
import { Button } from '@change/ds-core-ui/Button';
import { Link } from '@change/ds-core-ui/Link';
import { Modal, ModalBody, ModalFooter, ModalHeader } from '@change/ds-core-ui/Modal';
import { Text } from '@change/ds-core-ui/Text';
import { QuotationIcon } from '@change/ds-icons/QuotationIcon';

import { Translate, useI18n } from '@change-corgi/core-react/i18n';

import type { TopicPageSupporterVoiceComment } from '#src/app/pages/topic/details/pageContext';
import { HtmlComment } from '#src/app/shared/components/html';

import { useSupporterVoices } from '../../hooks/useSupporterVoices';
import { MessageLabel } from '../MessageLabel';

type TextMessageProp = {
	supporterVoice: TopicPageSupporterVoiceComment;
};

export const TextMessage: React.FC<TextMessageProp> = ({
	supporterVoice: {
		text: message,
		user: { firstName: user },
		petition,
		id: commentId,
	},
}) => {
	const { translate } = useI18n();
	const textRef = useRef<HTMLDivElement>(null);
	const [textClamped, setTextClamped] = useState<boolean>(false);

	const {
		data: { topicName },
	} = useSupporterVoices();

	useEffect(() => {
		const textEl = textRef.current;

		if (textEl) {
			const clamped = textEl.scrollHeight > textEl.clientHeight;
			setTextClamped(clamped);
		}
	}, [message]);

	// The petition can be null on the wire when it's been deleted, but in practice the middle-end
	// already drops supporter voices whose petition doesn't resolve, so this shouldn't happen here.
	if (!petition) return null;

	return (
		<div data-testid="supporter-voices-text" data-qa="supporter-voices-text-message">
			<div className="bg-background-raised text-typography-strong flex h-[326px] w-[75vw] max-w-[336px] flex-col gap-4 rounded-lg p-6">
				<QuotationIcon size={40} className="text-icon-strong" />
				<Text as="div" ref={textRef} className="line-clamp-7 italic" expressive>
					<HtmlComment html={message} />
				</Text>

				{textClamped && (
					<Modal
						trigger={
							// oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- not fixed to avoid regression risk from an automated markup rewrite; needs manual accessibility review
							<Link role="button" as="button" data-qa="text-message-show-more-link">
								<Translate value="corgi.topic_page.supporter_voices.show_text" />
							</Link>
						}
					>
						<ModalHeader leadingSlot={<Avatar size="large" src="" alt={user} />} title={user} />
						<ModalBody className="p-6">
							<HtmlComment html={message} />
						</ModalBody>
						<ModalFooter>
							<Button data-qa="modal-close-button" variant="secondary">
								<Translate value="corgi.topic_page.supporter_voices.close_modal" />
							</Button>
						</ModalFooter>
					</Modal>
				)}
			</div>
			<MessageLabel
				name={user}
				petition={{
					href: `/p/${petition.slug}?utm_source=topic_${topicName}_supporter_voices_text_comment_${commentId}`,
					title: petition.redacted ? translate('corgi.components.petition_card.under_review') : petition.ask,
				}}
			/>
		</div>
	);
};
